import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Projector, Layers, Wrench, Scan, ArrowRight,
  Globe, Mail, ChevronDown, Zap, Monitor, ShieldCheck, Gauge, Box
} from 'lucide-react';
import LandingHero3D from './LandingHero3D';
import LandingBuildingScene from './LandingBuildingScene';
import useInView from './useInView';
import './landing.css';

/**
 * Scroll-triggered reveal wrapper. Uses IntersectionObserver so each section
 * fades + slides up the first time it enters the viewport.
 */
const Reveal = ({ children, delay = 0, as: Tag = 'div', className = '', ...rest }) => {
  const [ref, visible] = useInView();
  return (
    <Tag
      ref={ref}
      className={`lp-reveal ${visible ? 'is-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * Long-form, cinematic landing page for the AV Simulation Tool.
 *
 * Structure:
 *   01 Hero              — Brand + headline + CTA + rotating projector-beam stage
 *   02 Capabilities      — 4 feature cards (Projection / LED / Truss / Simulation)
 *   03 Stage showcase    — Cinematic SVG stage with truss + 3 projector beams
 *   04 LED wall          — Animated tile grid
 *   05 Stats             — 3 big numbers that matter in AV
 *   06 Final CTA         — "Start 15-Day Free Demo"
 *   07 Contact / Footer  — Website + email + © block
 *
 * No auth required — accessible at /landing-preview.
 * All animations are CSS (no extra JS runtime cost, no Babylon canvas).
 */
export default function LandingPage() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-white overflow-x-hidden" data-testid="landing-page">
      {/* ────────── 01 HERO ────────── */}
      <Hero scrollY={scrollY} />

      {/* ────────── 02 CAPABILITIES ────────── */}
      <Capabilities />

      {/* ────────── 03 STAGE SHOWCASE ────────── */}
      <StageShowcase scrollY={scrollY} />

      {/* ────────── 04 LED WALL ────────── */}
      <LedWallSection />

      {/* ────────── 04.5 ARCHITECTURAL PROJECTION (BUILDING) ────────── */}
      <BuildingProjectionSection />

      {/* ────────── 05 STATS ────────── */}
      <StatsSection />

      {/* ────────── 06 FINAL CTA ────────── */}
      <FinalCTA />

      {/* ────────── 07 CONTACT + FOOTER ────────── */}
      <FooterSection />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  01 HERO                                                         */
/* ═══════════════════════════════════════════════════════════════ */

const Hero = ({ scrollY }) => (
  <section
    className="relative min-h-screen flex items-center justify-center overflow-hidden lp-grain"
    data-testid="landing-hero"
  >
    {/* Deep-space base */}
    <div className="absolute inset-0 bg-gradient-to-b from-black via-[#060817] to-zinc-950" />

    {/* ── Interactive Babylon 3D scene (truss + LED wall + projectors) ── */}
    <div className="absolute inset-0 z-0">
      <LandingHero3D className="w-full h-full" />
    </div>

    {/* Falling laser traces layered on top of the 3D scene */}
    <div className="absolute top-0 left-1/4 w-[2px] h-40 bg-gradient-to-b from-cyan-300 to-transparent lp-laser pointer-events-none" style={{ animationDelay: '0.8s' }} />
    <div className="absolute top-0 right-1/4 w-[2px] h-40 bg-gradient-to-b from-fuchsia-300 to-transparent lp-laser pointer-events-none" style={{ animationDelay: '1.6s' }} />

    {/* Vignette to ensure text readability over the 3D scene */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.55)_100%)] pointer-events-none" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/55 pointer-events-none" />

    <div
      className="relative z-10 max-w-5xl text-center px-4 sm:px-6 -mt-10 sm:-mt-24 lg:-mt-32"
      style={{ transform: `translateY(${scrollY * -0.15}px)` }}
    >
      {/* Brand mark */}
      <div className="flex justify-center mb-5 sm:mb-7 lp-fade-up">
        <img
          src="/branding/avst-logo.png"
          alt="AV Simulation Tool"
          className="h-28 sm:h-44 lg:h-56 w-auto select-none drop-shadow-[0_0_40px_rgba(56,189,248,0.45)]"
          draggable={false}
        />
      </div>

      {/* Eyebrow */}
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 backdrop-blur lp-fade-up" data-testid="hero-eyebrow">
        <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
        <span className="text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.2em] uppercase text-cyan-200">
          Projection · LED · Truss · Simulation
        </span>
      </div>

      {/* Headline */}
      <h1
        className="mt-5 sm:mt-6 text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] sm:leading-[1.05] lp-fade-up"
        style={{ animationDelay: '0.1s' }}
      >
        <span className="text-white">AV Simulation Tool</span>
        <br />
        <span className="lp-gold">for pros who Delivered in Reality.</span>
      </h1>

      <p
        className="mt-4 sm:mt-6 text-zinc-300 text-sm sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed lp-fade-up px-2 sm:px-0"
        style={{ animationDelay: '0.25s' }}
      >
        Plan projection mapping, LED walls, truss rigging and every beam path —
        with millimetre-precise 3D simulation — <em className="text-white not-italic">before</em> the first cable is run on site.
      </p>

      {/* CTAs */}
      <div
        className="mt-7 sm:mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center lp-fade-up"
        style={{ animationDelay: '0.4s' }}
      >
        <Link
          to="/signup"
          className="group inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-semibold text-sm sm:text-base shadow-[0_0_40px_rgba(34,211,238,0.35)] hover:shadow-[0_0_60px_rgba(217,70,239,0.55)] transition-all"
          data-testid="hero-cta-signup"
        >
          Start 15-Day Free Demo
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full border border-zinc-700 bg-zinc-900/70 text-zinc-200 hover:border-cyan-400/60 hover:text-white text-sm sm:text-base backdrop-blur transition-colors"
          data-testid="hero-cta-signin"
        >
          Sign In
        </Link>
      </div>

      <p className="mt-6 text-[11px] text-zinc-500 tracking-wide uppercase lp-fade-up hidden" style={{ animationDelay: '0.5s' }}>
        &nbsp;
      </p>

      {/* Scroll hint — hidden on small screens to avoid overlapping the CTAs */}
      <div className="hidden sm:flex absolute left-1/2 -translate-x-1/2 bottom-6 flex-col items-center gap-1 text-zinc-500 lp-float" data-testid="scroll-hint">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ChevronDown className="w-4 h-4" />
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════ */
/*  02 CAPABILITIES                                                 */
/* ═══════════════════════════════════════════════════════════════ */

const Capabilities = () => {
  const cards = [
    { icon: Projector, title: 'Projection Mapping', body: 'Map any building, dome or stage. Brightness, throw, keystone, reverse — all simulated.', accent: 'from-cyan-500/20 to-cyan-500/0', iconColor: 'text-cyan-300' },
    { icon: Layers, title: 'LED Wall Builder', body: 'Flat, curved, corner, soft-corner and flexible LED — thousands of tiles at 60fps.', accent: 'from-fuchsia-500/20 to-fuchsia-500/0', iconColor: 'text-fuchsia-300' },
    { icon: Wrench, title: 'Truss & Rigging', body: 'Load calculation, scaffolding, bracing — pull real truss specs into your scene.', accent: 'from-amber-500/20 to-amber-500/0', iconColor: 'text-amber-300' },
    { icon: Scan, title: '3D Simulation', body: 'Pre-vis every show with physically-accurate light, shadows, mirrors and camera paths.', accent: 'from-indigo-500/20 to-indigo-500/0', iconColor: 'text-indigo-300' }
  ];
  return (
    <section className="relative py-16 sm:py-24 lg:py-28 px-4 sm:px-6" data-testid="capabilities">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <SectionHeading eyebrow="Capabilities" title={<>Everything that lights a stage, <span className="lp-gold">in one canvas.</span></>} />
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8 sm:mt-12">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 100}>
              <div
                className="group relative p-4 sm:p-5 rounded-2xl bg-zinc-900/70 border border-zinc-800 hover:border-cyan-400/50 transition-colors overflow-hidden h-full"
                data-testid={`capability-${i}`}
              >
                <div className={`absolute -inset-px bg-gradient-to-br ${c.accent} opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl`} />
                <div className="relative">
                  <div className="h-10 w-10 rounded-xl bg-black/50 border border-zinc-800 flex items-center justify-center mb-3 sm:mb-4">
                    <c.icon className={`w-5 h-5 ${c.iconColor}`} />
                  </div>
                  <h3 className="text-base font-semibold text-white">{c.title}</h3>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/*  03 STAGE SHOWCASE                                               */
/* ═══════════════════════════════════════════════════════════════ */

const StageShowcase = ({ scrollY }) => (
  <section className="relative py-28 overflow-hidden" data-testid="stage-showcase">
    {/* Backdrop */}
    <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-[#050612] to-zinc-950" />
    <div className="absolute inset-0 lp-grid-bg opacity-30" />

    <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-12 items-center">
      {/* Copy column */}
      <Reveal className="lg:col-span-2">
        <SectionHeading
          eyebrow="Pre-visualize"
          align="left"
          title={<>Every truss, projector & tile — <span className="lp-gold">rendered to scale.</span></>}
        />
        <p className="mt-6 text-zinc-400 leading-relaxed">
          Pull real projector brightness curves, real LED pixel pitches, and
          real truss load limits. Walk the camera through your show before
          anyone loads in.
        </p>
        <ul className="mt-6 space-y-2">
          {[
            'Accurate lux / brightness calculation',
            'Recursive mirror reflections',
            'Custom shadow atlas for 60+ projectors',
            'ViewCube, saved cameras, live share link'
          ].map(x => (
            <li key={x} className="flex items-start gap-2 text-sm text-zinc-300">
              <ShieldCheck className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
              {x}
            </li>
          ))}
        </ul>
      </Reveal>

      {/* SVG stage */}
      <Reveal delay={150} className="lg:col-span-3">
        <div
          className="relative aspect-[16/10] rounded-2xl border border-zinc-800 bg-[radial-gradient(ellipse_at_top,rgba(34,211,238,0.1),transparent_70%)] overflow-hidden"
          style={{ transform: `translateY(${Math.min(0, (scrollY - 800) * -0.05)}px)` }}
        >
          <StageSVG />
          {/* Corner glow accents */}
          <div className="absolute top-0 left-0 w-40 h-40 bg-cyan-400/10 blur-3xl rounded-full" />
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-fuchsia-500/10 blur-3xl rounded-full" />
        </div>
      </Reveal>
    </div>
  </section>
);

// A hand-drawn SVG of a stage: truss, 3 projectors with beams, and a hit surface.
const StageSVG = () => (
  <svg viewBox="0 0 800 500" className="absolute inset-0 w-full h-full">
    <defs>
      {/* Projector beam gradients */}
      <linearGradient id="beamA" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.75" />
        <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="beamB" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#d946ef" stopOpacity="0.75" />
        <stop offset="100%" stopColor="#d946ef" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="beamC" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f6d98d" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#f6d98d" stopOpacity="0" />
      </linearGradient>
      {/* Stage floor */}
      <linearGradient id="floor" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0b0f1f" />
        <stop offset="100%" stopColor="#000" />
      </linearGradient>
    </defs>

    {/* Floor */}
    <polygon points="60,420 740,420 680,480 120,480" fill="url(#floor)" stroke="#1f2937" />

    {/* Back LED wall */}
    <g>
      <rect x="220" y="200" width="360" height="170" fill="#060a14" stroke="#1f2937" />
      {/* LED tiles */}
      {Array.from({ length: 12 }).map((_, i) =>
        Array.from({ length: 6 }).map((__, j) => (
          <rect
            key={`${i}-${j}`}
            x={220 + i * 30}
            y={200 + j * 28}
            width="28"
            height="26"
            fill={((i + j) % 3 === 0) ? '#22d3ee' : ((i + j) % 3 === 1 ? '#d946ef' : '#111827')}
            opacity={((i + j) % 3 === 0) ? 0.55 : ((i + j) % 3 === 1 ? 0.5 : 1)}
            stroke="#1f2937"
          />
        ))
      )}
    </g>

    {/* Truss — horizontal top */}
    <g stroke="#3a4458" strokeWidth="1.2" fill="none">
      <line x1="120" y1="90" x2="680" y2="90" />
      <line x1="120" y1="120" x2="680" y2="120" />
      {Array.from({ length: 22 }).map((_, i) => (
        <line key={i} x1={120 + i * 26} y1="90" x2={120 + i * 26} y2="120" />
      ))}
      {Array.from({ length: 21 }).map((_, i) => (
        <line key={i} x1={120 + i * 26} y1="90" x2={120 + (i + 1) * 26} y2="120" />
      ))}
    </g>

    {/* Truss uprights */}
    <g stroke="#3a4458" strokeWidth="1.2" fill="none">
      <line x1="120" y1="90" x2="120" y2="420" />
      <line x1="680" y1="90" x2="680" y2="420" />
      <line x1="140" y1="90" x2="140" y2="420" />
      <line x1="660" y1="90" x2="660" y2="420" />
      {Array.from({ length: 10 }).map((_, i) => (
        <line key={`lh-${i}`} x1="120" y1={90 + i * 34} x2="140" y2={90 + i * 34} />
      ))}
      {Array.from({ length: 10 }).map((_, i) => (
        <line key={`rh-${i}`} x1="660" y1={90 + i * 34} x2="680" y2={90 + i * 34} />
      ))}
    </g>

    {/* Projectors on truss */}
    {[
      { x: 220, beam: 'beamA' },
      { x: 400, beam: 'beamC' },
      { x: 580, beam: 'beamB' }
    ].map((p, i) => (
      <g key={i}>
        <rect x={p.x - 18} y="120" width="36" height="22" rx="2" fill="#0f172a" stroke="#475569" />
        <rect x={p.x - 6} y="142" width="12" height="8" fill="#22d3ee" />
        {/* Beam cone */}
        <polygon
          points={`${p.x - 3},150 ${p.x + 3},150 ${p.x + 80},385 ${p.x - 80},385`}
          fill={`url(#${p.beam})`}
        />
      </g>
    ))}

    {/* Audience silhouette */}
    <g fill="#111827">
      {Array.from({ length: 14 }).map((_, i) => (
        <ellipse key={i} cx={90 + i * 48} cy="470" rx="16" ry="5" />
      ))}
    </g>

    {/* Scale grid on floor */}
    <g stroke="#1e2b3a" strokeWidth="0.5" opacity="0.6">
      {Array.from({ length: 10 }).map((_, i) => (
        <line key={i} x1={80 + i * 70} y1="420" x2={140 + i * 64} y2="478" />
      ))}
    </g>
  </svg>
);

/* ═══════════════════════════════════════════════════════════════ */
/*  04 LED WALL SECTION                                             */
/* ═══════════════════════════════════════════════════════════════ */

const LedWallSection = () => (
  <section className="relative py-16 sm:py-24 lg:py-28 px-4 sm:px-6" data-testid="led-section">
    <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
      {/* Animated LED tile mosaic */}
      <Reveal>
        <div className="relative rounded-2xl border border-zinc-800 bg-black p-4 overflow-hidden">
          <div className="grid gap-[3px]" style={{ gridTemplateColumns: 'repeat(18, minmax(0, 1fr))' }}>
            {Array.from({ length: 18 * 10 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square lp-led"
                style={{ animationDelay: `${(i % 18) * 0.08 + Math.floor(i / 18) * 0.12}s` }}
              />
            ))}
          </div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.7)_100%)] pointer-events-none" />
          <div className="absolute bottom-3 left-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            LIVE PIXEL PREVIEW · 18×10 tiles
          </div>
        </div>
      </Reveal>

      <Reveal delay={150}>
        <SectionHeading
          eyebrow="LED Engine"
          align="left"
          title={<>10,000+ tiles. <span className="lp-gold">Zero lag.</span></>}
        />
        <p className="mt-5 text-zinc-400 leading-relaxed">
          A custom thin-instance renderer + shared shadow atlas handles flat,
          curved, corner, soft-corner and flexible LED in the same scene — at
          60 fps, even when every tile plays a different video.
        </p>
        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          {[
            { k: '10K+', v: 'LED tiles', icon: Monitor },
            { k: '60 fps', v: 'Render', icon: Gauge },
            { k: '5', v: 'Wall modes', icon: Box }
          ].map((s, i) => (
            <div key={i} className="p-3 rounded-xl bg-zinc-900/70 border border-zinc-800">
              <s.icon className="w-4 h-4 text-cyan-300 mx-auto mb-1" />
              <div className="text-xl font-bold text-white">{s.k}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{s.v}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════ */
/*  04.5 ARCHITECTURAL PROJECTION (BUILDING)                        */
/* ═══════════════════════════════════════════════════════════════ */

const BuildingProjectionSection = () => (
  <section className="relative py-16 sm:py-24 lg:py-28 px-4 sm:px-6 overflow-hidden" data-testid="building-projection-section">
    <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-[#04060f] to-zinc-950" />
    <div className="absolute inset-0 lp-grid-bg opacity-25" />

    <div className="relative max-w-7xl mx-auto grid lg:grid-cols-5 gap-8 sm:gap-10 lg:gap-12 items-center">
      {/* Copy column */}
      <Reveal className="lg:col-span-2">
        <SectionHeading
          eyebrow="Architectural Mapping"
          align="left"
          title={<>Map entire buildings <span className="lp-gold">before the truck rolls out.</span></>}
        />
        <p className="mt-6 text-zinc-400 leading-relaxed">
          From cathedral facades to corporate towers — import a mesh, drop
          ground-stacked projectors, and pre-visualise every beam, blend and
          edge-mask at real-world scale.
        </p>
        <ul className="mt-6 space-y-2">
          {[
            'Works on any imported .fbx / .glb / .obj mesh',
            'Stacked / ground / flown projector rigs',
            'Per-projector lens shift, zoom & keystone',
            'Live brightness roll-off on the facade'
          ].map(x => (
            <li key={x} className="flex items-start gap-2 text-sm text-zinc-300">
              <ShieldCheck className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
              {x}
            </li>
          ))}
        </ul>
      </Reveal>

      {/* 3D Building canvas */}
      <Reveal delay={150} className="lg:col-span-3">
        <div
          className="relative aspect-[16/10] rounded-2xl border border-zinc-800 bg-black overflow-hidden"
          data-testid="building-projection-canvas-wrapper"
        >
          <LandingBuildingScene className="absolute inset-0" />
          {/* Corner glow accents */}
          <div className="absolute top-0 left-0 w-40 h-40 bg-cyan-400/10 blur-3xl rounded-full pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-fuchsia-500/10 blur-3xl rounded-full pointer-events-none" />
          <div className="absolute bottom-3 left-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Live preview · 3 projectors · building mesh
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════ */
/*  05 STATS                                                        */
/* ═══════════════════════════════════════════════════════════════ */

const StatsSection = () => (
  <section className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 bg-gradient-to-b from-zinc-950 via-[#080a18] to-zinc-950" data-testid="stats">
    <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center">
      {[
        { k: '500+', v: 'projector + lens combos' },
        { k: '0.1 lx', v: 'brightness precision' },
        { k: '60+', v: 'simultaneous projections' }
      ].map((s, i) => (
        <Reveal key={i} delay={i * 120}>
          <div className="p-6 sm:p-8 rounded-2xl border border-zinc-800 bg-zinc-900/40 backdrop-blur h-full">
            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold lp-gold">{s.k}</div>
            <div className="mt-2 text-zinc-400 text-xs sm:text-sm uppercase tracking-widest">{s.v}</div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════ */
/*  06 FINAL CTA                                                    */
/* ═══════════════════════════════════════════════════════════════ */

const FinalCTA = () => (
  <section className="relative py-16 sm:py-24 lg:py-28 px-4 sm:px-6 overflow-hidden" data-testid="final-cta">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-fuchsia-600/20 lp-glow pointer-events-none" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-cyan-500/15 lp-glow pointer-events-none" style={{ animationDelay: '2s' }} />

    <div className="relative max-w-3xl mx-auto text-center">
      <Reveal>
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold leading-tight">
          Ready to design your next show <span className="lp-gold">in light?</span>
        </h2>
        <p className="mt-3 sm:mt-4 text-zinc-400 text-sm sm:text-base lg:text-lg px-2 sm:px-0">
          Start a free 15-day demo. Everything unlocked. No payment, no card.
        </p>
        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/signup"
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-semibold text-sm sm:text-base shadow-[0_0_40px_rgba(34,211,238,0.35)] hover:shadow-[0_0_60px_rgba(217,70,239,0.55)] transition-all"
            data-testid="final-cta-signup"
          >
            Start Free Demo <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full border border-zinc-700 bg-zinc-900/70 hover:border-cyan-400/60 text-zinc-200 hover:text-white text-sm sm:text-base transition-colors"
            data-testid="final-cta-signin"
          >
            Sign In
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════ */
/*  07 FOOTER                                                       */
/* ═══════════════════════════════════════════════════════════════ */

const FooterSection = () => (
  <footer className="relative px-4 sm:px-6 pt-12 sm:pt-16 pb-8 sm:pb-10 border-t border-zinc-900 bg-black" data-testid="landing-footer">
    <div className="max-w-5xl mx-auto text-center space-y-4 sm:space-y-5">
      <div>
        <div className="text-lg sm:text-xl font-bold tracking-tight text-white">AV Simulation Tool</div>
        <div className="text-xs text-zinc-500 mt-1">Powered by Ideation Audio Visual</div>
      </div>

      <div className="pt-3 sm:pt-4">
        <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-[0.3em]">For more details, contact us</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center items-center text-xs sm:text-sm">
          <a href="https://www.ideationav.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-zinc-300 hover:text-cyan-300 transition-colors break-all">
            <Globe className="w-4 h-4 shrink-0" /> www.ideationav.com
          </a>
          <span className="hidden sm:inline text-zinc-700">•</span>
          <a href="mailto:admin@ideationav.com" className="inline-flex items-center gap-2 text-zinc-300 hover:text-cyan-300 transition-colors break-all">
            <Mail className="w-4 h-4 shrink-0" /> admin@ideationav.com
          </a>
        </div>
      </div>

      <div className="pt-5 sm:pt-6 border-t border-zinc-900 text-[10px] sm:text-[11px] text-zinc-600 leading-relaxed">
        <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center mb-3 text-zinc-500">
          <Link to="/terms" className="hover:text-cyan-300 transition-colors" data-testid="footer-terms-link">
            Terms &amp; Conditions
          </Link>
          <span className="text-zinc-700">|</span>
          <Link to="/terms#privacy-data-security" className="hover:text-cyan-300 transition-colors" data-testid="footer-privacy-link">
            Privacy Policy
          </Link>
          <span className="text-zinc-700">|</span>
          <a href="mailto:admin@ideationav.com" className="hover:text-cyan-300 transition-colors" data-testid="footer-support-link">
            Support
          </a>
        </div>
        © 2026 Ideation Audio Visual. All rights reserved.<br />
        Unauthorized use, reproduction, or distribution is strictly prohibited.
      </div>
    </div>
  </footer>
);

/* ─────────── shared bits ─────────── */

const SectionHeading = ({ eyebrow, title, align = 'center' }) => (
  <div className={align === 'left' ? 'text-left' : 'text-center'}>
    <div className={`text-[10px] tracking-[0.35em] uppercase text-cyan-300 ${align === 'left' ? '' : 'flex items-center justify-center gap-2'}`}>
      <Zap className="w-3 h-3 inline mr-1" />{eyebrow}
    </div>
    <h2 className="mt-3 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
      {title}
    </h2>
  </div>
);
